import {
    GetServerSidePropsContext
} from 'next';
import * as builder from 'fetch-retry-ts';
import dataDogLogger from './lib/datadog';

const fetchWithRetry = builder.default(fetch, {
    retries: 4,
    retryOn: [429, 503, 504],
    retryDelay: (attempt: number, error: Error | null, response: Response | null): number => {
        return Math.pow(2, attempt) * 500;
    }
});

const logFetchTiming = (message: string, time: number, path: string) => {
    dataDogLogger('info', `${message} -> loaded in ${time} ms`, {
        frontendMetric: time,
        process: path
    });
};

const responseToJson = async (response: Response, path?: string) => {
    const contentType = response.headers.get('content-type');
    const data = contentType && contentType.indexOf('application/json') > -1 ? await response.json() : {};
    dataDogLogger(
        'info',
        `[networking] ${path ?? response.url} ${response.status}`,
        {
            rawData: data
        }
    );
    return data;
};

/**
 * Utility to generate URL based on apiResource and
 * @param apiResource - resource name. If "foo", then "api.cutbackcoach.com/foo" is called.
 * @param context - GetServerSidePropsContext if called from SSR, null if called from client side
 *
 * Exported for logic/patient-appointment-request.ts, which needs the HTTP status and error body that
 * get/post/put/del discard; host resolution is subtle enough that a second copy would drift.
 */
export function generateUrl(apiResource: string, params?: Record<string, string>, context?: GetServerSidePropsContext): URL {
    const domain = getWebsiteDomain(context);
    let host = '';
    if (!domain) {
        host = process.env.NEXT_PUBLIC_SUNNYSIDE_API_HOST;
    } else {
        const env = process.env.NEXT_PUBLIC_NENV;
        const apiSubdomain = env === 'development' ? '.dev' : env === 'staging' ? '.staging' : '';
        const port = env === 'development' ? ':8080' : '';
        const protocol = env !== 'development' ? 'https' : 'http';
        host = `${protocol}://api${apiSubdomain}${domain}${port}/`;
    }

    const url = new URL(host + apiResource);
    if (params) {
        Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
    }
    return url;
}

export type Data = Record<string, string | unknown | number>;

/**
 * Utility to parse ServerSideProps context for SSR
 * @param context GetServerSidePropsContext for nextjs SSR
 */
export function parseFetchOptionFrom(method: string, context?: GetServerSidePropsContext, payload?: Record<string, unknown> | any[]): Record<string, unknown> {
    const credentials: RequestCredentials = 'include';
    const fetchOption: any = {
        method: method,
        credentials: credentials
    };

    // Use plain object instead of Headers constructor for better compatibility
    const headers: Record<string, string> = {};
    headers['SS-Platform'] = 'web';

    if (context) {
        // List of headers that should NOT be forwarded to outgoing requests
        const excludedHeaders = [
            'connection',
            'upgrade',
            'host',
            'x-real-ip',
            'x-forwarded-for',
            'x-forwarded-proto',
            'x-forwarded-port',
            'x-amzn-trace-id'
        ];

        Object.keys(context.req.headers).forEach((key: string) => {
            // Skip excluded headers
            if (excludedHeaders.includes(key.toLowerCase())) {
                return;
            }

            const value = context.req.headers[key];
            const parsedValue = typeof value === 'string'
                ? value
                : Array.isArray(value) ? value.join(',') : String(value);
            headers[key] = parsedValue;
        });
    } else {
        headers['Content-Type'] = 'application/json';
    }

    if (payload) {
        fetchOption.body = JSON.stringify(payload);
        headers['Content-Type'] = 'application/json';
    }

    fetchOption.headers = headers;
    return fetchOption;
}

/**
 * Cutback coach "rest-api" GET request utility
 * @param apiResource - resource name. If "foo", then "api.cutbackcoach.com/foo" is called.
 * @param params - queryParams
 * @param context - GetServerSidePropsContext if called from SSR, null if called from client side
 */
export async function get(apiResource: string, params?: Record<string, string>, context?: GetServerSidePropsContext): Promise<any> {
    const startTime = new Date().getTime();
    const url = generateUrl(apiResource, params, context);

    try {
        const res = await fetchWithRetry(
            url.toString(),
            parseFetchOptionFrom('GET', context)
        );
        const endTime = new Date().getTime();

        logFetchTiming(`Get request for ${apiResource}`, endTime - startTime, apiResource);

        return responseToJson(res, apiResource);
    } catch (e) {
        dataDogLogger(
            'error',
            `Frontend Fetch Error: ${e.message} Stack: ${e.stack}`,
            {
                url: url.toString(),
                rawData: {
                    params,
                    errorName: e.name,
                    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
                    visibilityState: typeof document !== 'undefined' ? document.visibilityState : null
                }
            }
        );
        throw e;
    }
}

/**
 * Cutback coach "rest-api" POST request utility
 * @param apiResource - resource name. If "foo", then "api.cutbackcoach.com/foo" is called.
 * @param params - queryParams
 * @param context - GetServerSidePropsContext if called from SSR, null if called from client side
 */
export async function post(apiResource: string, payload?: Data | any[], params?: Record<string, string>, context?: GetServerSidePropsContext): Promise<Data> {
    const startTime = new Date().getTime();
    const url = generateUrl(apiResource, params, context);

    const res = await fetchWithRetry(
        url.toString(),
        parseFetchOptionFrom('POST', context, payload)
    );
    const endTime = new Date().getTime();

    logFetchTiming(`Post request for ${apiResource}`, endTime - startTime, apiResource);

    return responseToJson(res, apiResource);
}

/**
 * Cutback coach "rest-api" PUT request utility
 * @param apiResource - resource name. If "foo", then "api.cutbackcoach.com/foo" is called.
 * @param params - queryParams
 * @param context - GetServerSidePropsContext if called from SSR, null if called from client side
 */
export async function put(apiResource: string, payload: Data, params?: Record<string, string>, context?: GetServerSidePropsContext): Promise<Data> {
    const startTime = new Date().getTime();
    const url = generateUrl(apiResource, params, context);

    const res = await fetchWithRetry(
        url.toString(),
        parseFetchOptionFrom('PUT', context, payload)
    );
    const endTime = new Date().getTime();

    logFetchTiming(`Put request for ${apiResource}`, endTime - startTime, apiResource);

    return responseToJson(res, apiResource);
}

/**
 * Cutback coach "rest-api" DELETE request utility
 * @param apiResource - resource name. If "foo", then "api.cutbackcoach.com/foo" is called.
 * @param params - queryParams
 * @param context - GetServerSidePropsContext if called from SSR, null if called from client side
 */
export async function del(apiResource: string, params ?: Record < string, string >, context ?: GetServerSidePropsContext): Promise < Data > {
    const startTime = new Date().getTime();
    const url = generateUrl(apiResource, params, context);

    const res = await fetchWithRetry(
        url.toString(),
        parseFetchOptionFrom('DELETE', context)
    );
    const endTime = new Date().getTime();

    logFetchTiming(`Delete request for ${apiResource}`, endTime - startTime, apiResource);

    return responseToJson(res, apiResource);
}

/**
 * Utility to get UI domain. Designed for setting proper domain for cookies.
 * 
 * Usage:
 * - Client-side: getWebsiteDomain() - uses window.location.hostname
 * - Server-side: getWebsiteDomain(context) - uses context.req.headers.host from getServerSideProps
 * 
 * @param context - Optional GetServerSidePropsContext for server-side host detection
 */
export function getWebsiteDomain(context?: GetServerSidePropsContext): string {
    let hostName = '';
    // Try to get hostname from request headers (works on both client and server)
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
        // Client-side fallback
        hostName = window.location.hostname || '';
        if (hostName === 'localhost') {
            return hostName;
        }
    } else {
        // Server-side: try to get host from context if available
        if (context?.req?.headers?.host) {
            hostName = context.req.headers.host.split(':')[0]; // Remove port if present
        } else {
            // Fallback for environments where context isn't available
            hostName = '';
        }
    }

    /**
     * app.cutbackcoach.com
     * cutbackcoach.com
     *
     * matches cutbackcoach.com
     */
    const match = hostName.match(/(\S+\.)?([\S]+\.com?)$/);
    const processedDomain = match ? '.' + match[2] : '';

    return processedDomain;
}
